import { useRef, useState, useEffect } from "react";
import { Play } from "lucide-react";
import { useTeleprompterLines } from "@/hooks/useTeleprompterLines";
import { ScriptSettings } from "@/hooks/useScripts";

interface PreviewDisplayProps {
  content: string;
  settings: ScriptSettings;
  sampleText: string;
  className?: string;
}

export function PreviewDisplay({ content, settings, sampleText, className }: PreviewDisplayProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollOffsetRef = useRef(0);
  const [containerWidth, setContainerWidth] = useState(0);
  
  // Measure container width for visual line calculations
  useEffect(() => {
    if (!scrollRef.current) return;
    const updateWidth = () => {
        if (scrollRef.current) {
            setContainerWidth(scrollRef.current.clientWidth);
        }
    };
    updateWidth();
    // Use ResizeObserver for more robust width tracking
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(scrollRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Use a fallback text if content is empty or too short, but prefer actual content
  const displayContent = content.trim().length > 0 
    ? content 
    : (sampleText || "範例文字\n測試多行顯示\n效果預覽\n這是一段為了\n測試捲動速度\n而生成的\n長文本範例");
  
  // Ensure enough content to scroll by repeating it if it's short
  const repeatedContent = displayContent.length < 100 
    ? `${displayContent}\n\n${displayContent}\n\n${displayContent}` 
    : displayContent;

  // Use the SAME hook as TeleprompterDisplay for visual line splitting
  const paddingPx = containerWidth * ((settings.horizontalPadding ?? 10) / 100);
  
  const lines = useTeleprompterLines({
    content: repeatedContent,
    fontSize: Math.max(12, settings.fontSize * 0.4), // Pass scaled font size!
    containerWidth,
    paddingLeft: paddingPx,
    paddingRight: paddingPx,
  });

  useEffect(() => {
    const container = scrollRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    
    let animationFrameId: number;
    // Calibrated speed factor: 0.01 maps 50 speed to ~30px/sec (~150-200 WPM)
    const speedFactor = (settings.scrollSpeed || 50) * 0.01; 
    
    const animate = () => {
      // Update offset
      scrollOffsetRef.current += speedFactor;
      
      // Check for loop condition
      // If we've scrolled past the bottom (minus some buffer), reset
      // Note: scrollHeight is the height of content, clientHeight is the height of viewport
      if (scrollOffsetRef.current + container.clientHeight >= container.scrollHeight - 10) {
         scrollOffsetRef.current = 0;
      }
      
      // Apply transform
      const transform = `translate3d(0, -${scrollOffsetRef.current}px, 0)${settings.mirrorMode ? ' scaleX(-1)' : ''}`;
      content.style.transform = transform;
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    // Reset offset when content/settings change to avoid jumping
    // Or keep it? keeping it is better for live editing, but might be out of bounds.
    // Let's safe guard checking bounds in the loop, but maybe reset on unmount or specific changes?
    // For now, let's just ensure we don't start out of bounds if possible, 
    // but relying on the loop to fix it is safe enough.
    
    animationFrameId = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrameId);
  }, [settings.scrollSpeed, settings.mirrorMode, lines]); // Re-run when speed, mirror mode or calculated lines change

  return (
    <div
      className={`rounded-lg w-full flex items-center justify-center border-2 border-foreground overflow-hidden relative ${className || 'h-40'}`}
      style={{ backgroundColor: settings.bgColor }}
    >
      {/* Top Gradient Overlay */}
      <div 
        className="absolute top-0 left-0 w-full z-10 pointer-events-none"
        style={{
          height: '40%',
          background: `linear-gradient(to bottom, ${settings.bgColor} 10%, transparent 100%)`
        }}
      />

      <div 
        ref={scrollRef}
        className="w-full h-full overflow-hidden relative scrollbar-hide"
        style={{
          scrollBehavior: "auto", 
        }}
      >
        <div 
          ref={contentRef}
          className="w-full text-center"
          style={{
             paddingTop: "40%", 
             paddingBottom: "40%", 
             paddingLeft: `${settings.horizontalPadding ?? 10}%`,
             paddingRight: `${settings.horizontalPadding ?? 10}%`,
             willChange: "transform",
          }}
        >
          {lines.map((line, index) => {
            return (
              <div
                key={index}
                style={{
                  fontSize: `${Math.max(12, settings.fontSize * 0.4)}px`, // Scaled down
                  lineHeight: settings.lineHeight ?? 1.5,
                  paddingTop: "0.1em",
                  paddingBottom: "0.1em",
                  color: settings.fontColor,
                  opacity: 1,
                  fontWeight: 600, // Constant weight
                  whiteSpace: "pre-wrap",
                  wordBreak: "keep-all"
                }}
              >
                {line || "\u00A0"}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Bottom Gradient Overlay */}
      <div 
        className="absolute bottom-0 left-0 w-full z-10 pointer-events-none"
        style={{
          height: '40%',
          background: `linear-gradient(to top, ${settings.bgColor} 10%, transparent 100%)`
        }}
      />

      {/* Visual cue for scrolling */}
      <div className="absolute top-2 right-2 opacity-50 pointer-events-none z-20">
        <Play className="h-3 w-3 text-foreground" fill="currentColor" />
      </div>
    </div>
  );
}
